import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import { patchPreferences } from '@/api/me';

const SERVER_KEY = 'savedTablePreferences';

export interface TablePrefs {
  vis?: Record<string, boolean>;
  size?: Record<string, number>;
  order?: string[];
  pinning?: { left?: string[]; right?: string[] };
  density?: string;
  gridlines?: boolean;
  sorting?: string | undefined;
  filters?: string | undefined;
  groupBy?: string | null | undefined;
  rowsPerPage?: number;
}

type ServerEntry = { table: string; [k: string]: unknown };

/** One column's saved layout, in the shape the server has always stored. */
interface ColumnReordering {
  column: string;
  show: boolean;
  width: number;
}

/** What the server falls back to, and what a new entry is created with. */
const DEFAULT_ROWS_PER_PAGE = 50;

/**
 * v2's own namespace for the `table` field.
 *
 * `badges`, `documents`, `management-users` and `supply-chain-mapping` are
 * storage keys in *both* frontends, and the two describe different tables: the
 * column ids in a v2 `columnReordering` mean nothing to the old dashboard's
 * table of the same name, and vice versa. Sharing the entry would have each app
 * hiding and resizing columns the other has never heard of, so v2 writes
 * alongside rather than into the old entries.
 */
const V2_TABLE_PREFIX = 'v2:';

const serverTableName = (storageKey: string) =>
  `${V2_TABLE_PREFIX}${storageKey}`;

/**
 * Where v2 keeps the preferences the stored shape has no column for.
 *
 * `filter` is typed `z.record(z.string(), z.unknown())`, so it takes arbitrary
 * nested data, and it is the only field in the entry that does. Sorting,
 * density, grid lines, pinning and grouping ride inside it under this one
 * reserved key rather than going missing — the alternative was widening
 * `SavedTableSettingSchema`, which is a shared contract and a backend deploy.
 *
 * It is safe precisely because v2's entries are namespaced (`V2_TABLE_PREFIX`):
 * the old dashboard reads `filter` as a flat map of column → values and never
 * looks at a `v2:` entry, so nothing of its own gains a key it does not
 * understand.
 */
const V2_EXTRAS_KEY = '__v2';

/** The preferences carried inside `filter`, having no field of their own. */
type TableExtras = Pick<
  TablePrefs,
  'sorting' | 'density' | 'gridlines' | 'pinning' | 'groupBy'
>;

const EXTRA_KEYS = [
  'sorting',
  'density',
  'gridlines',
  'pinning',
  'groupBy',
] as const;

function parseJson<T>(raw: string | undefined, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/**
 * A table's preferences → the stored entry.
 *
 * The server keeps four fields — `table`, `rowsPerPage`, `columnReordering` and
 * `filter` — and a plain `z.object` silently strips the rest, so those four are
 * all there is to write to. It is enough: `columnReordering` is a list of
 * `{ column, show, width }`, which carries visibility, width *and* order in one
 * field, exactly as the old dashboard has always used it
 * (`frontend/src/hooks/useTable.jsx`).
 *
 * `density`, `gridlines`, `pinning`, `sorting` and `groupBy` have no field to go
 * to and are deliberately not sent. They stay in memory, so they survive
 * navigation within a session, and reset on reload — which is also what the old
 * dashboard does with them.
 */
function toServerEntry(
  storageKey: string,
  prefs: TablePrefs,
  previous: ServerEntry | undefined,
): ServerEntry {
  const entry: ServerEntry = {
    ...previous,
    table: serverTableName(storageKey),
  };

  entry.rowsPerPage =
    prefs.rowsPerPage ??
    (typeof previous?.rowsPerPage === 'number'
      ? previous.rowsPerPage
      : DEFAULT_ROWS_PER_PAGE);

  // Written only from a complete column list — `DataTable` resolves an untouched
  // order to the natural one before saving, so the array always describes every
  // column and its position is meaningful on the way back.
  const order = prefs.order ?? [];
  if (order.length) {
    entry.columnReordering = order.map<ColumnReordering>(column => ({
      column,
      show: prefs.vis?.[column] ?? true,
      width: prefs.size?.[column] ?? 0,
    }));
  }

  // `filters` is the table's JSON string; `filter` is the object the server
  // holds, and it also carries the extras. Built from the table's whole
  // preferences rather than the field that changed, so saving a filter cannot
  // drop the sort riding alongside it.
  const filter: Record<string, unknown> = {
    ...parseJson<Record<string, unknown>>(prefs.filters, {}),
  };
  const extras: TableExtras = {};
  for (const key of EXTRA_KEYS) {
    if (prefs[key] !== undefined) {
      (extras as Record<string, unknown>)[key] = prefs[key];
    }
  }
  if (Object.keys(extras).length > 0) filter[V2_EXTRAS_KEY] = extras;
  if (Object.keys(filter).length > 0) {
    entry.filter = filter;
  } else if (previous?.filter) {
    // Explicitly null rather than omitted: the entry is replaced wholesale, and
    // "the user cleared the filter" has to be distinguishable from "this save
    // had nothing to say about it".
    entry.filter = null;
  } else {
    delete entry.filter;
  }

  return entry;
}

/** The stored entry → a table's preferences. The inverse of `toServerEntry`. */
function fromServerEntry(entry: ServerEntry): TablePrefs {
  const prefs: TablePrefs = {};

  if (typeof entry.rowsPerPage === 'number') {
    prefs.rowsPerPage = entry.rowsPerPage;
  }

  const saved = entry.columnReordering;
  if (Array.isArray(saved) && saved.length > 0) {
    const vis: Record<string, boolean> = {};
    const size: Record<string, number> = {};
    const order: string[] = [];
    for (const item of saved as Partial<ColumnReordering>[]) {
      if (!item?.column) continue;
      order.push(item.column);
      // Recorded for every column, not just the hidden ones: a table can hide a
      // column by default, and "the user turned that one back on" is only
      // expressible as an explicit `true`.
      vis[item.column] = item.show !== false;
      // A width of 0 means "never dragged" — leaving it out lets the column keep
      // its natural size instead of collapsing.
      if (typeof item.width === 'number' && item.width > 0) {
        size[item.column] = item.width;
      }
    }
    prefs.order = order;
    prefs.vis = vis;
    if (Object.keys(size).length > 0) prefs.size = size;
  }

  const filter = entry.filter;
  if (filter && typeof filter === 'object') {
    const { [V2_EXTRAS_KEY]: extras, ...columns } = filter as Record<
      string,
      unknown
    >;
    if (extras && typeof extras === 'object') {
      Object.assign(prefs, extras as TableExtras);
    }
    if (Object.keys(columns).length > 0) {
      prefs.filters = JSON.stringify(columns);
    }
  }

  return prefs;
}

/**
 * The server requires `rowsPerPage` on *every* entry in the array, and the whole
 * patch is one document — so a single stored entry missing it (written before
 * the field existed) makes every later save 400, and no preference for any
 * table is ever kept again. The write is fire-and-forget, so that failure is
 * invisible from the UI: preferences simply stop sticking. The old dashboard
 * fills the same gap for the same reason (`frontend/src/hooks/useTable.jsx`).
 */
function withRowsPerPage(entry: ServerEntry): ServerEntry {
  return typeof entry.rowsPerPage === 'number'
    ? entry
    : { ...entry, rowsPerPage: DEFAULT_ROWS_PER_PAGE };
}

/**
 * Table preferences live on the user, not on the device.
 *
 * They used to be mirrored into `localStorage` — per field, plus a combined map,
 * plus a copy of the server array — with the server treated as a backup. That
 * made the device the source of truth between seeds: a preference saved in one
 * browser was invisible in another until the next login.
 *
 * There is one copy in memory now, seeded from the account during
 * `bootstrapSession` and written back on change. What *reaches* the account is
 * whatever the stored shape has room for — see `toServerEntry`: visibility,
 * width, order, the active filter and the page size persist; density, grid
 * lines, pinning, sorting and grouping are session-only, as they are in the old
 * dashboard.
 */
let prefsByTable: Record<string, TablePrefs> = {};
/** The array as the server stores it, kept so a write preserves other tables. */
let serverEntries: ServerEntry[] = [];
/**
 * Whether this session has already taken its copy from the account.
 *
 * The root route's `beforeLoad` calls `bootstrapSession` on *every* navigation,
 * so without this the seed re-ran constantly and replaced memory with whatever
 * the account last stored. Changing a filter or a sort navigates
 * (`useTable` writes them to the URL), which put a re-seed squarely between the
 * change and the debounced write a second later — so the two preferences that
 * navigate were the two that never saved, while density and column widths,
 * which change nothing in the URL, saved fine.
 *
 * After the first seed the browser is the writer: `clearTablePreferences` re-arms
 * it, and the session store calls that when the signed-in user changes.
 */
let seeded = false;

export function getTablePrefs(storageKey: string): TablePrefs {
  return prefsByTable[storageKey] ?? {};
}

export function clearTablePreferences(): void {
  // Session-scoped: drops what this tab holds. The account's own preferences
  // are untouched, so the next `bootstrapSession` seeds them back — which is
  // what logout wants, and what a "reset my tables" action would not.
  prefsByTable = {};
  serverEntries = [];
  seeded = false;
}

/**
 * Loads the account's saved table preferences into memory.
 *
 * Called from `bootstrapSession`, which `__root.tsx` awaits in `beforeLoad` —
 * so every table mounts with them already in place, the same guarantee the
 * seed-into-localStorage step used to give.
 */
export function seedTablePreferencesFromSession(
  preferences: Record<string, unknown>,
) {
  if (seeded) return;
  const serverPrefs = preferences[SERVER_KEY];
  if (!serverPrefs || !Array.isArray(serverPrefs)) return;
  seeded = true;

  const entries = serverPrefs as ServerEntry[];
  // Kept whole, old-dashboard entries included, so writing one table back never
  // drops another app's.
  serverEntries = entries;

  const map: Record<string, TablePrefs> = {};
  for (const entry of entries) {
    const { table } = entry;
    if (typeof table !== 'string' || !table.startsWith(V2_TABLE_PREFIX)) {
      continue;
    }
    const storageKey = table.slice(V2_TABLE_PREFIX.length);
    map[storageKey] = { ...map[storageKey], ...fromServerEntry(entry) };
  }
  prefsByTable = map;
}

/** Writes one table's in-memory preferences back to the account. */
function writeTableToBackend(tableKey: string): Promise<void> {
  const arr = [...serverEntries];
  const name = serverTableName(tableKey);
  const idx = arr.findIndex(e => e.table === name);
  // The whole table is written each time, not just the field that changed —
  // the preference is one entry and the patch replaces the array.
  const entry = toServerEntry(tableKey, prefsByTable[tableKey] ?? {}, arr[idx]);
  if (idx >= 0) arr[idx] = entry;
  else arr.push(entry);

  const payload = arr.map(withRowsPerPage);
  serverEntries = payload;
  return patchPreferences(SERVER_KEY, payload).then(
    () => undefined,
    () => {
      // A rejected write must not surface as an unhandled rejection: the user
      // did not ask for this request, and a failed one is not worth a toast
      // over a column width. It stays in memory, so the tab keeps the
      // preference and the next change tries again.
    },
  );
}

/**
 * Forgets a table's saved filters, in memory and on the account.
 *
 * For a saved filter the API refuses: it is restored into the URL on every
 * visit, so until it is gone the page fails on load in any browser. Written
 * straight away rather than debounced — the caller is about to navigate, and
 * the next mount must not find the filter again.
 */
export function clearSavedFilters(storageKey: string): Promise<void> {
  const { filters: _dropped, ...rest } = prefsByTable[storageKey] ?? {};
  prefsByTable[storageKey] = rest;
  return writeTableToBackend(storageKey);
}

export function useTablePreferences(storageKey: string) {
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readyRef = useRef(false);

  // Note: seedTablePreferencesFromSession runs during bootstrapSession, which
  // __root.tsx awaits, so memory is populated before any DataTable mounts.

  useEffect(() => {
    const t = setTimeout(() => {
      readyRef.current = true;
    }, 500);
    return () => clearTimeout(t);
  }, []);

  const syncToBackend = useCallback((tableKey: string) => {
    if (!readyRef.current) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      void writeTableToBackend(tableKey);
    }, 1000);
  }, []);

  const save = useCallback(
    (prefs: TablePrefs) => {
      prefsByTable[storageKey] = { ...prefsByTable[storageKey], ...prefs };
      syncToBackend(storageKey);
    },
    [storageKey, syncToBackend],
  );

  return { save };
}

/**
 * On first mount, restores saved sorting/filters from prefs into the URL
 * if the URL has neither. `mapPrefs` converts TablePrefs → partial search params.
 */
export function useRestorePrefsToSearch<
  TSearch extends Record<string, unknown>,
>(
  storageKey: string,
  opts: {
    hasExistingSearch: boolean;
    mapPrefs: (prefs: TablePrefs) => Partial<TSearch>;
    /** When false, the restore is skipped entirely. Used by `useTable` when
     *  it runs in `mode: 'state'` (no URL involvement). Defaults to true. */
    enabled?: boolean;
  },
) {
  const navigate = useNavigate();
  const restoredRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs once on mount
  useEffect(() => {
    if (opts.enabled === false) return;
    if (restoredRef.current || opts.hasExistingSearch) return;
    restoredRef.current = true;
    const prefs = getTablePrefs(storageKey);
    const patch = opts.mapPrefs(prefs);
    if (Object.keys(patch).length === 0) return;
    navigate({
      search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }),
      replace: true,
    } as Parameters<typeof navigate>[0]);
  }, []);
}
